import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '../../contexts/AuthContext'
import { dbHelpers } from '../../lib/supabase'
import SafeIcon from '../../common/SafeIcon'
import PerformanceMetrics from './PerformanceMetrics'
import TradingHeatmap from './TradingHeatmap'
import SetupAnalysis from './SetupAnalysis'
import * as FiIcons from 'react-icons/fi'

const { FiCalendar, FiDownload } = FiIcons

const Analytics = () => {
  const { user } = useAuth()
  const [trades, setTrades] = useState([])
  const [loading, setLoading] = useState(true)
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  })

  useEffect(() => {
    if (user) {
      loadAnalyticsData()
    }
  }, [user, dateRange])

  const loadAnalyticsData = async () => {
    try {
      setLoading(true)
      const tradesData = await dbHelpers.getTradingStats(
        user.id,
        dateRange.from,
        dateRange.to
      )
      setTrades(tradesData)
    } catch (error) {
      console.error('Error loading analytics data:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6"
      >
        <header>
          <h1 className="text-4xl font-black tracking-tight text-gray-900 dark:text-white bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-400">
            Trading Analytics
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-2 font-medium">
            Detailed analysis of your professional performance
          </p>
        </header>
        
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-2 bg-white dark:bg-slate-900/50 px-4 py-2.5 rounded-2xl border border-gray-200 dark:border-white/5 shadow-sm">
            <SafeIcon icon={FiCalendar} className="h-5 w-5 text-lux-primary" />
            <input 
              type="date" 
              value={dateRange.from}
              onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
              className="bg-transparent border-none text-sm font-bold focus:ring-0 outline-none dark:text-white"
            />
            <span className="text-gray-400">to</span>
            <input 
              type="date" 
              value={dateRange.to}
              onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
              className="bg-transparent border-none text-sm font-bold focus:ring-0 outline-none dark:text-white"
            />
          </div>
          <button className="flex items-center space-x-2 lux-button-secondary py-3 px-6">
            <SafeIcon icon={FiDownload} className="h-4 w-4" />
            <span>Export</span>
          </button>
        </div>
      </motion.div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
           <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-lux-primary"></div>
        </div>
      ) : (
        <>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <PerformanceMetrics trades={trades} />
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <TradingHeatmap trades={trades} />
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <SetupAnalysis trades={trades} />
            </motion.div>
          </div>
        </>
      )}
    </div>
  )
}

export default Analytics